import { useEpiserver } from '@episerver/spa-core';
import { Typography } from '@norges-domstoler/dds-components';

type ImageBreakPoint = { code: 'img'; imgWidth: string | number; format?: string; order?: number };
type SourceBreakPoint = { code: string; cssMedia: string; imgWidth: string | number; format?: string; order?: number };
export type Breakpoint = ImageBreakPoint | SourceBreakPoint;

export type ResponsiveImageProps = React.DetailedHTMLProps<
    React.ImgHTMLAttributes<HTMLImageElement>,
    HTMLImageElement
> & {
    breakpoints: Breakpoint[];
    alt: string;
    aspectRatio?: string;
    key?: string;
    quality?: number;
    upscale?: boolean;
    caption?: string;
};

let responsiveImageId: number = 0;

function parseDimension(dimension: number | string): [number, string] {
    if (typeof dimension === 'number') return [dimension, 'px'];
    const size = parseFloat(dimension);
    if (isNaN(size)) return [undefined, 'px'];
    const unit = dimension.replace(size.toString(), '');
    return [size, unit];
}

export const ResponsiveContentImage = (
    props: ResponsiveImageProps & {
        imgUrl: string;
    }
) => {
    return <ResponsiveImage alt={props.alt} {...props} src={props.imgUrl} />;
};

export const ResponsiveImage = (props: ResponsiveImageProps) => {
    if (props.srcSet) return <img {...props} />; //We already have a responsive definition, so keep that.
    const imgQuality = props.quality || 75;
    const keyprefix = `${props.key || 'picture'}-${++responsiveImageId}-`;

    const sources = props.breakpoints
        .sort((a, b) => {
            return (a.order || 0) - (b.order || 0);
        })
        .map((bp) => {
            const key = keyprefix + '-' + bp.code;
            if (bp.code === 'img')
                return (
                    <ResponsiveImageImg
                        key={key}
                        src={props.src}
                        bp={bp as ImageBreakPoint}
                        ratio={props.aspectRatio}
                        imgQuality={imgQuality}
                        keyprefix={key}
                        className={props.className}
                        alt={props.alt}
                    />
                );
            else
                return (
                    <ResponsiveImageSource
                        key={key}
                        src={props.src}
                        bp={bp as SourceBreakPoint}
                        ratio={props.aspectRatio}
                        imgQuality={imgQuality}
                        keyprefix={key}
                    />
                );
        })
        .filter((x) => x);

    return (
        <figure>
            <picture>{sources}</picture>
            {props.caption && (
                <Typography className="p-1" typographyType="bodySmall" as="figcaption">
                    {props.caption}
                </Typography>
            )}
        </figure>
    );
};

const ResponsiveImageSource = (props: {
    src: string;
    bp: SourceBreakPoint;
    keyprefix: string;
    ratio: string;
    imgQuality: number;
}) => {
    const bp = props.bp;
    const keyprefix = props.keyprefix;
    const ratio = props.ratio;
    const imgQuality = props.imgQuality;
    const ctx = useEpiserver();
    const imgUrl = new URL(props.src, ctx.getEpiserverURL());

    const srcProps: React.SourceHTMLAttributes<HTMLSourceElement> & { key?: string } = {
        media: bp.cssMedia,
        style: {},
    };

    let key = keyprefix + bp.code;

    if (bp.format) {
        srcProps.type = 'image/' + bp.format;
        imgUrl.searchParams.set('format', bp.format);
        key += '-' + bp.format;
    }
    if (bp.imgWidth) {
        const [imgWidth, imgWidthUnit] = parseDimension(bp.imgWidth);
        if (imgWidthUnit.toLowerCase() === 'px') {
            imgUrl.searchParams.set('width', imgWidth.toString());
            if (ratio) {
                const [h, v] = ratio.split('/', 2).map((x) => parseFloat(x));
                if (h && v) {
                    const imgHeight = Math.round((imgWidth / h) * v);
                    imgUrl.searchParams.set('height', imgHeight.toString());
                    imgUrl.searchParams.set('mode', 'crop');
                    srcProps.width = imgWidth;
                    srcProps.height = imgHeight;
                }
            }
        }
    }
    if (props.imgQuality) {
        imgUrl.searchParams.set('quality', imgQuality.toString());
    }
    if (ratio) srcProps.style.aspectRatio = ratio;
    srcProps.srcSet = imgUrl.href;

    return <source key={key} {...srcProps} />;
};

const ResponsiveImageImg = (props: {
    src: string;
    bp: ImageBreakPoint;
    keyprefix: string;
    ratio: string;
    imgQuality: number;
    className?: string;
    alt?: string;
}) => {
    const bp = props.bp;
    const keyprefix = props.keyprefix;
    const ratio = props.ratio;
    const imgQuality = props.imgQuality;
    const ctx = useEpiserver();
    const imgUrl = new URL(props.src, ctx.getEpiserverURL());
    imgUrl.searchParams.set('upscale', 'false');

    const srcProps: React.ImgHTMLAttributes<HTMLImageElement> & { key?: string } = {
        className: props.className,
        alt: props.alt,
        style: {},
    };

    let key = keyprefix + bp.code;

    if (bp.format) {
        imgUrl.searchParams.set('format', bp.format);
        key += '-' + bp.format;
    }

    if (bp.imgWidth) {
        const [imgWidth, imgWidthUnit] = parseDimension(bp.imgWidth);
        if (imgWidthUnit.toLowerCase() === 'px') {
            imgUrl.searchParams.set('width', imgWidth.toString());
            if (ratio) {
                const [h, v] = ratio.split('/', 2).map((x) => parseFloat(x));
                if (h && v) {
                    const imgHeight = Math.round((imgWidth / h) * v);
                    imgUrl.searchParams.set('height', imgHeight.toString());
                    imgUrl.searchParams.set('mode', 'crop');
                    srcProps.width = imgWidth;
                    srcProps.height = imgHeight;
                }
            }
        }
    }
    if (props.imgQuality) {
        imgUrl.searchParams.set('quality', imgQuality.toString());
    }

    if (ratio) srcProps.style.aspectRatio = ratio;
    srcProps.src = imgUrl.href;

    return <img key={key} {...srcProps} />;
};

export default ResponsiveImage;
